import { useRef, useImperativeHandle, forwardRef, useState, useEffect } from "react";
import SignaturePad from "react-signature-pad-wrapper";
import { Eraser, Pencil, X } from "lucide-react";
import { ExButton } from "../../../components/ExButton";
import { Overlay, ModalContainer, Header, Title, CloseButton, SignatureWrapper, Actions } from "./styles";

interface Props {
    onSave: (signature: string) => void;
}

export interface SignatureModalRef {
    openModal: () => void;
}

export const SignatureModal = forwardRef<SignatureModalRef, Props>(
    ({ onSave }, ref) => {
        const sigPad = useRef<any>(null);
        const wrapperRef = useRef<HTMLDivElement>(null);
        const [open, setOpen] = useState(false);

        function clear() { sigPad.current?.clear() }

        function close() { setOpen(false) }

        function resizeCanvas() {
            if (!wrapperRef.current) return;

            const canvas = wrapperRef.current.querySelector("canvas") as HTMLCanvasElement | null;

            if (!canvas) return;

            const ratio = Math.max(window.devicePixelRatio || 1, 1);

            const width = wrapperRef.current.offsetWidth;
            const height = wrapperRef.current.offsetHeight;

            canvas.width = width * ratio;
            canvas.height = height * ratio;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;

            const ctx = canvas.getContext("2d");
            ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);

            sigPad.current?.clear();
        }

        function save() {
            if (!sigPad.current || sigPad.current.isEmpty()) return;

            const signature = sigPad.current.toDataURL("image/png");
            onSave(signature);
            close();
        }

        useEffect(() => {
            if (!open) return;

            function handleKeyDown(e: KeyboardEvent) {
                if (e.key === "Escape") close();
            }

            window.addEventListener("keydown", handleKeyDown);
            return () => window.removeEventListener("keydown", handleKeyDown);
        }, [open]);

        useImperativeHandle(ref, () => ({
            openModal: () => {
                setOpen(true);

                setTimeout(() => {
                    resizeCanvas();
                }, 50);
            },
        }));

        if (!open) return null;

        return (
            <Overlay onClick={close}>
                <ModalContainer onClick={(e) => e.stopPropagation()}>
                    <Header>
                        <Title>
                            <Pencil size={18} />
                            Assinatura
                        </Title>

                        <CloseButton onClick={close}>
                            <X size={18} />
                        </CloseButton>
                    </Header>

                    <SignatureWrapper
                        ref={wrapperRef}
                        onTouchStart={(e) => e.preventDefault()}
                        onTouchMove={(e) => e.preventDefault()}
                    >
                        <SignaturePad
                            ref={sigPad}
                            options={{
                                penColor: "black",
                                minWidth: 1.2,
                                maxWidth: 2.5,
                                velocityFilterWeight: 0.8,
                            }}
                        />
                    </SignatureWrapper>

                    <Actions>
                        <ExButton
                            iconcenter={<Eraser size={18} />}
                            color="#c75353"
                            onClick={clear}
                        >
                            Limpar
                        </ExButton>

                        <ExButton onClick={save}>
                            Salvar Assinatura
                        </ExButton>
                    </Actions>
                </ModalContainer>
            </Overlay>
        );
    }
);
